import { get } from 'svelte/store';
import {
  activeCandidateId,
  candidates,
  exampleLoaded,
  exampleVersion,
  friends,
  friendsRaw,
  mapView,
  persist,
  resetStores,
  suggestions,
  trips,
  tripsRaw,
} from './state';
import { AMENITIES, CANDIDATE_COLORS, haversineKm } from './scoring';
import type { AmenityResult, Candidate, Friend, TravelMode, Trip } from './types';

export const EXAMPLE_DATA_VERSION = 8;
export const EXAMPLE_MAP_VIEW = { lat: 37.7612, lng: -122.4307, zoom: 13 } as const;

const EXAMPLE_FRIENDS: Friend[] = [
  { id: 'example_friend_maya_priya', name: 'Maya & Priya', address: '24th St & Castro St, San Francisco, CA', lat: 37.7515, lng: -122.4347 },
  { id: 'example_friend_luis_mateo', name: 'Luis & Mateo', address: '18th St & Valencia St, San Francisco, CA', lat: 37.7616, lng: -122.4214 },
  { id: 'example_friend_aisha', name: 'Aisha', address: 'Alamo Square, San Francisco, CA', lat: 37.7764, lng: -122.4346 },
  { id: 'example_friend_kenji', name: 'Kenji', address: '9th Ave & Irving St, San Francisco, CA', lat: 37.7638, lng: -122.4662 },
  { id: 'example_friend_nia', name: 'Nia', address: '22nd St & 3rd St, San Francisco, CA', lat: 37.7575, lng: -122.3885 },
  { id: 'example_friend_jordan', name: 'Jordan', address: 'Haight St & Ashbury St, San Francisco, CA', lat: 37.7693, lng: -122.4481 },
  { id: 'example_friend_eli_rowan', name: 'Eli & Rowan', address: 'Cortland Ave & Mission St, San Francisco, CA', lat: 37.7412, lng: -122.4220 },
  { id: 'example_friend_zuri', name: 'Zuri', address: 'Lake Merritt, Oakland, CA', lat: 37.8036, lng: -122.2588 },
  { id: 'example_friend_dev', name: 'Dev', address: 'Shattuck Ave & Vine St, Berkeley, CA', lat: 37.8802, lng: -122.2693 },
  { id: 'example_friend_sofia_camille', name: 'Sofía & Camille', address: 'Miller Ave & Throckmorton Ave, Mill Valley, CA', lat: 37.9061, lng: -122.5450 },
  { id: 'example_friend_marcus', name: 'Marcus', address: 'Linda Mar Blvd & Highway 1, Pacifica, CA', lat: 37.5944, lng: -122.5044 },
];

const EXAMPLE_TRIPS: Trip[] = [
  { id: 'example_trip_alvarado', name: 'Alvarado Elementary School', address: '625 Douglass St, San Francisco, CA 94114', lat: 37.7535, lng: -122.4380 },
  { id: 'example_trip_harvey_milk', name: 'Harvey Milk Civil Rights Academy', address: '4235 19th St, San Francisco, CA 94114', lat: 37.7586, lng: -122.4351 },
  { id: 'example_trip_rooftop', name: 'Rooftop School TK–8', address: '443 Burnett Ave, San Francisco, CA 94131', lat: 37.7545, lng: -122.4431 },
  { id: 'example_trip_dolores', name: 'Mission Dolores Park', address: '19th St & Dolores St, San Francisco, CA', lat: 37.7596, lng: -122.4269 },
  { id: 'example_trip_sfmoma', name: 'SFMOMA', address: '151 3rd St, San Francisco, CA 94103', lat: 37.7857, lng: -122.4011 },
  { id: 'example_trip_ferry', name: 'Ferry Building', address: '1 Ferry Building, San Francisco, CA 94111', lat: 37.7955, lng: -122.3937 },
  { id: 'example_trip_ggp', name: 'Golden Gate Park', address: 'JFK Promenade, San Francisco, CA', lat: 37.7694, lng: -122.4862 },
  { id: 'example_trip_sharon_art', name: 'Sharon Art Studio', address: '300 Bowling Green Dr, San Francisco, CA 94117', lat: 37.7682, lng: -122.4586 },
  { id: 'example_trip_dolores_tennis', name: 'Dolores Park Tennis Courts', address: '18th St & Dolores St, San Francisco, CA 94114', lat: 37.7611, lng: -122.4270 },
  { id: 'example_trip_stonemill', name: 'Stonemill Matcha', address: '561 Valencia St, San Francisco, CA 94110', lat: 37.7645, lng: -122.4217 },
  { id: 'example_trip_arsicault', name: 'Arsicault Bakery', address: '397 Arguello Blvd, San Francisco, CA 94118', lat: 37.7834, lng: -122.4593 },
  { id: 'example_trip_duboce_dog', name: 'Duboce Park Dog Play Area', address: 'Duboce Ave & Scott St, San Francisco, CA 94117', lat: 37.7694, lng: -122.4337 },
];

const CANDIDATE_INPUTS = [
  { id: 'example_candidate_castro', name: 'Castro garden flat', address: '18th St & Sanchez St, San Francisco, CA', lat: 37.7612, lng: -122.4307 },
  { id: 'example_candidate_hayes', name: 'Hayes Valley walk-up', address: 'Hayes St & Octavia Blvd, San Francisco, CA', lat: 37.7765, lng: -122.4247 },
  { id: 'example_candidate_sunset', name: 'Inner Sunset studio', address: '9th Ave & Irving St, San Francisco, CA', lat: 37.7640, lng: -122.4661 },
  { id: 'example_candidate_nopa', name: 'NoPa Victorian', address: 'Divisadero St & McAllister St, San Francisco, CA', lat: 37.7784, lng: -122.4386 },
  { id: 'example_candidate_bernal', name: 'Bernal cottage', address: 'Cortland Ave & Andover St, San Francisco, CA', lat: 37.7391, lng: -122.4165 },
  { id: 'example_candidate_oakland', name: 'Oakland lakeside loft', address: 'Grand Ave & Harrison St, Oakland, CA', lat: 37.8110, lng: -122.2605 },
] as const;

const MODE_FACTORS: Record<TravelMode, { perKm: number; base: number }> = {
  WALKING: { perKm: 13, base: 1 },
  BICYCLING: { perKm: 5, base: 2 },
  TRANSIT: { perKm: 4.2, base: 8 },
  DRIVING: { perKm: 3.2, base: 5 },
};

function commuteResults(candidate: { lat: number; lng: number }): Candidate['results'] {
  return Object.fromEntries(
    (Object.entries(MODE_FACTORS) as [TravelMode, { perKm: number; base: number }][]).map(([mode, factor]) => [
      mode,
      EXAMPLE_FRIENDS.map((friend) => {
        const km = haversineKm(candidate, friend);
        return { name: friend.name, minutes: Math.max(3, Math.round(km * factor.perKm + factor.base)), text: `${km.toFixed(1)} km` };
      }),
    ]),
  ) as Candidate['results'];
}

function amenitiesFor(index: number): Record<string, AmenityResult> {
  const minutes: Record<string, number[]> = {
    groceryMajor: [7, 9, 6, 8, 10, 7],
    groceryAny: [4, 5, 3, 5, 5, 4],
    transit: [5, 4, 6, 6, 8, 5],
    parkLarge: [10, 14, 7, 11, 9, 12],
    parkMedium: [6, 8, 5, 7, 6, 8],
    parkSmall: [4, 5, 4, 4, 3, 5],
    cafe: [3, 4, 5, 4, 5, 4],
    bar: [5, 6, 12, 5, 8, 7],
    convenience: [4, 5, 4, 4, 5, 4],
    restaurant: [3, 4, 5, 4, 4, 4],
    tennis: [9, 12, 8, 10, 7, 11],
    pickleball: [14, 16, 12, 13, 15, 18],
    basketball: [8, 10, 6, 9, 7, 9],
    soccer: [12, 15, 9, 13, 11, 14],
    track: [16, 18, 12, 15, 14, 19],
    pool: [13, 15, 11, 14, 12, 16],
    gym: [7, 9, 6, 8, 10, 8],
    climbing: [18, 22, 15, 20, 17, 24],
    playground: [5, 6, 4, 5, 4, 6],
    dogPark: [8, 9, 7, 8, 6, 10],
  };
  const names: Record<string, string> = {
    groceryMajor: 'Major grocery', groceryAny: 'Neighborhood market', transit: 'Muni stop',
    parkLarge: 'Big park', parkMedium: 'Neighborhood park', parkSmall: 'Pocket park',
    cafe: 'Cafe', bar: 'Bar', convenience: 'Corner store', restaurant: 'Restaurant',
    tennis: 'Tennis courts', pickleball: 'Pickleball courts', basketball: 'Basketball court', soccer: 'Soccer field',
    track: 'Running track', pool: 'Public pool', gym: 'Gym', climbing: 'Climbing gym',
    playground: 'Playground', dogPark: 'Dog park',
  };
  const areas: Record<string, number> = { parkLarge: 1_650_000, parkMedium: 65_000, parkSmall: 4_000 };
  return Object.fromEntries(AMENITIES.map((amenity) => [amenity.key, {
    minutes: minutes[amenity.key]?.[index] ?? 10,
    name: names[amenity.key] ?? amenity.label,
    ...(areas[amenity.key] ? { areaSqm: areas[amenity.key] } : {}),
  }]));
}

function tripResults(candidate: { lat: number; lng: number }): Candidate['trips'] {
  return Object.fromEntries(EXAMPLE_TRIPS.map((trip) => {
    const km = haversineKm(candidate, trip);
    return [trip.id, {
      driving: { minutes: Math.round(7 + km * 3.1) },
      transit: { minutes: Math.round(12 + km * 5.2) },
    }];
  }));
}

export function hasMapData(): boolean {
  return get(friends).length + get(trips).length + get(candidates).length > 0;
}

export function loadExampleData(): void {
  const exampleCandidates: Candidate[] = CANDIDATE_INPUTS.map((input, index) => ({
    ...input,
    color: CANDIDATE_COLORS[index % CANDIDATE_COLORS.length],
    results: commuteResults(input),
    amenities: amenitiesFor(index),
    trips: tripResults(input),
  }));

  friends.set(EXAMPLE_FRIENDS.map((friend) => ({ ...friend })));
  trips.set(EXAMPLE_TRIPS.map((trip) => ({ ...trip })));
  candidates.set(exampleCandidates);
  friendsRaw.set(EXAMPLE_FRIENDS.map((friend) => `${friend.name} | ${friend.address}`).join('\n'));
  tripsRaw.set(EXAMPLE_TRIPS.map((trip) => `${trip.name} | ${trip.address}`).join('\n'));
  suggestions.set([]);
  activeCandidateId.set(exampleCandidates[0].id);
  exampleLoaded.set(true);
  exampleVersion.set(EXAMPLE_DATA_VERSION);
  mapView.set({ ...EXAMPLE_MAP_VIEW });
  persist();
}

export function resetExampleData(): void {
  resetStores();
  mapView.set({ ...EXAMPLE_MAP_VIEW });
  persist();
}
